import Enumerable from "linq";
import { FormatStructureEditor } from "../FormatStructureEditor";
import { IFormatAlert } from "../IFormatAlert";
import { IFormatObjectEditable } from "../IFormatObjectEditable";
import { ObjectAlert } from "../alerts/ObjectAlert";
import {
  instanceOfICalculationExpressionBehaviorPropertyEditable,
  instanceOfIConditionalExpressionBehaviorPropertyEditable,
  instanceOfIDateDurationExpressionBehaviorPropertyEditable,
  instanceOfITimeSpanExpressionBehaviorPropertyEditable,
  instanceOfIDateComparisonExpressionBehaviorPropertyEditable,
  instanceOfITextJoinBehaviorPropertyEditable,
  instanceOfILineGraphBehaviorPropertyEditable,
  instanceOfITranscribeBehaviorPropertyEditable,
  instanceOfIDataCalculateBehaviorPropertyEditable,
  instanceOfIItemSelectTriggerBehaviorPropertyEditable,
  instanceOfITranscribeDateOfTimeBehaviorPropertyEditable,
  instanceOfIEmptyAlertAllowBehaviorPropertyEditable,
  instanceOfIGroupAlertAllowBehaviorPropertyEditable,
  instanceOfIScriptExecuteTriggerBehaviorPropertyEditable,
  instanceOfIScriptExecuteBehaviorPropertyEditable,
  instanceOfIHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
} from "../properties";
import {
  instanceOfILinkedObjectSpecifiable,
  ILinkedObjectSpecifiable,
  ILinkedPageObjectSpecifiable,
  ILineGraphItem,
  ILineGraphLinkedObjectSpecifiable,
  IDateCalculateItem,
  IDateCalculateObject,
  instanceOfIDateCalculateObject,
  ILinkedObjectGroupSpecifiable,
  ICalculationExpressionBehavior,
  ITimeSpanExpressionBehavior,
  IObjectControllableFeature,
  IConditionalExpressionBehavior,
  IDateDurationExpressionBehavior,
  IDateComparisonExpressionBehavior,
  ITextJoinBehavior,
  ILineGraphBehavior,
  IObjectsControllable,
  IDateCalculateBehavior,
  ILinkedScriptExecuteBehavior,
  IHospitalizedHisInformationItemSelectBehavior,
} from "../../../../documaker/domain/format/objects";
import { IPropertyEditable } from "../../../../base/domain/system/edit";
import { AbstractFormatValidator } from "./AbstractFormatValidator";

export class LinkedPageObjectValidator extends AbstractFormatValidator {
  constructor() {
    super();
  }

  public validate(format: FormatStructureEditor): IFormatAlert[] {
    const linkedList = this.getLinkedPageObjectList(format);
    return this.concat(...this.extractObjects(format).map(o => this.validateObject(o, linkedList)));
  }

  private getLinkedPageObjectList(format: FormatStructureEditor): ILinkedPageObjectSpecifiable[] {
    return this.processObjects(format, (o, i) => this.toLinkedPageObjectSpecifiable(o, i + 1));
  }

  private toLinkedPageObjectSpecifiable(object: IFormatObjectEditable, pageKey: number): ILinkedPageObjectSpecifiable {
    const linkedObject = object.toLinkedObjectIdentifier();
    return {
      pageKey: pageKey,
      type: linkedObject.type,
      id: linkedObject.id,
      key: linkedObject.key,
      subKey: linkedObject.subKey,
      tableIndex: linkedObject.tableIndex,
      isDate: linkedObject.isDate,
    };
  }

  private validateObject(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return [
      ...this.validateCalculationExpression(object, linkedList),
      ...this.validateConditionalExpression(object, linkedList),
      ...this.validateDateDurationExpression(object, linkedList),
      ...this.validateTimeSpanExpression(object, linkedList),
      ...this.validateDateComparisonExpression(object, linkedList),
      ...this.validateTextJoin(object, linkedList),
      ...this.validateLineGraph(object, linkedList),
      ...this.validateTranscribe(object, linkedList),
      ...this.validateDataCalculate(object, linkedList),
      ...this.validateItemSelectTrigger(object, linkedList),
      ...this.validateTranscribeDateOfTime(object, linkedList),
      ...this.validateEmptyAlertAllow(object, linkedList),
      ...this.validateGroupAlertAllow(object, linkedList),
      ...this.validateScriptExecuteTrigger(object, linkedList),
      ...this.validateScriptExecute(object, linkedList),
      ...this.validateHospitalizedHisInformationItemSelect(object, linkedList),
    ].filter(a => !!a);
  }

  private validateCalculationExpression(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<ICalculationExpressionBehavior>(
      instanceOfICalculationExpressionBehaviorPropertyEditable(object) ? object.calculationExpressionBehavior() : undefined,
      v => [
        ...this.validateLinkedObjects(object, linkedList, Enumerable.from(v.contents)
          .select(o => instanceOfILinkedObjectSpecifiable(o.reference) ? o.reference : null)
          .toArray(), "計算対象項目"),
        ...this.validateLinkedObjects(object, linkedList, Enumerable.from(v.conditionalExpressionBehavior.contents)
          .select(o => instanceOfILinkedObjectSpecifiable(o.reference) ? o.reference : null)
          .toArray(), "計算条件", "(比較元)"),
        ...this.validateLinkedObjects(object, linkedList, Enumerable.from(v.conditionalExpressionBehavior.contents)
          .select(o => instanceOfILinkedObjectSpecifiable(o.presentation.reference) ? o.presentation.reference : null)
          .toArray(), "計算条件", "(比較先)"),
      ],
    );
  }

  private validateConditionalExpression(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IConditionalExpressionBehavior>(
      instanceOfIConditionalExpressionBehaviorPropertyEditable(object) ? object.conditionalExpressionBehavior() : undefined,
      v => [
        ...this.validateLinkedObjects(object, linkedList, Enumerable.from(v.contents)
          .select(o => instanceOfILinkedObjectSpecifiable(o.reference) ? o.reference : null)
          .toArray(), "条件式項目", "(比較元)", false),
        ...this.validateLinkedObjects(object, linkedList, Enumerable.from(v.contents)
          .select(o => instanceOfILinkedObjectSpecifiable(o.presentation.reference) ? o.presentation.reference : null)
          .toArray(), "条件式項目", "(値)", false),
      ],
    );
  }

  private validateDateDurationExpression(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IDateDurationExpressionBehavior>(
      instanceOfIDateDurationExpressionBehaviorPropertyEditable(object) ? object.dateDurationExpressionBehavior() : undefined,
      v => [
        this.validateLinkedObject(object, linkedList, v.from, "期間日数項目", "(開始日)"),
        this.validateLinkedObject(object, linkedList, v.to, "期間日数項目", "(終了日)"),
      ],
    );
  }

  private validateTimeSpanExpression(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<ITimeSpanExpressionBehavior>(
      instanceOfITimeSpanExpressionBehaviorPropertyEditable(object) ? object.timeSpanExpressionBehavior() : undefined,
      v => [
        this.validateLinkedObject(object, linkedList, v.from, "経過時間項目", "(開始日時)"),
        this.validateLinkedObject(object, linkedList, v.to, "経過時間項目", "(終了日時)"),
      ],
    );
  }

  private validateDateComparisonExpression(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IDateComparisonExpressionBehavior>(
      instanceOfIDateComparisonExpressionBehaviorPropertyEditable(object) ? object.dateComparisonExpressionBehavior() : undefined,
      v => [
        ...this.validateLinkedObjects(object, linkedList, Enumerable.from(v.contents)
          .select(o => instanceOfILinkedObjectSpecifiable(o.reference) ? o.reference : null)
          .toArray(), "比較項目"),
      ],
    );
  }

  private validateTextJoin(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<ITextJoinBehavior>(
      instanceOfITextJoinBehaviorPropertyEditable(object) ? object.textJoinBehavior() : undefined,
      v => [
        ...this.validateLinkedObjects(object, linkedList, Enumerable.from(v.items)
          .select(o => instanceOfILinkedObjectSpecifiable(o.reference) ? o.reference : null)
          .toArray(), "結合テキスト項目"),
      ],
    );
  }

  private validateLineGraph(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<ILineGraphBehavior>(
      instanceOfILineGraphBehaviorPropertyEditable(object) ? object.lineGraphBehavior() : undefined,
      v => [
        ...this.validateLineGraphItems(object, linkedList, v.items),
      ],
    );
  }

  private validateTranscribe(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IObjectsControllable<ILinkedPageObjectSpecifiable>>(
      instanceOfITranscribeBehaviorPropertyEditable(object) ? object.transcribeBehavior() : undefined,
      v => [
        ...this.validateLinkedPageObjects(object, linkedList, v.targets, "文書内転記の設定", "", false),
      ],
    );
  }

  private validateDataCalculate(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IDateCalculateBehavior>(
      instanceOfIDataCalculateBehaviorPropertyEditable(object) ? object.dateCalculateBehavior() : undefined,
      v => [
        ...this.validateDateCalculateItems(object, linkedList, v.items),
      ],
    );
  }

  private validateItemSelectTrigger(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IObjectsControllable<ILinkedPageObjectSpecifiable>>(
      instanceOfIItemSelectTriggerBehaviorPropertyEditable(object) ? object.itemSelectTriggerBehavior() : undefined,
      v => [
        ...this.validateLinkedPageObjects(object, linkedList, v.targets, "ユーザ定義連動入力の設定"),
      ],
    );
  }

  private validateTranscribeDateOfTime(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IObjectControllableFeature<ILinkedPageObjectSpecifiable>>(
      instanceOfITranscribeDateOfTimeBehaviorPropertyEditable(object) ? object.transcribeDateOfTimeBehavior() : undefined,
      v => [
        v.isEnable ? this.validateLinkedPageObject(object, linkedList, v.target, "日付の連動入力の設定") : undefined,
      ],
    );
  }

  private validateEmptyAlertAllow(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IObjectsControllable<ILinkedPageObjectSpecifiable>>(
      instanceOfIEmptyAlertAllowBehaviorPropertyEditable(object) ? object.emptyAlertAllowBehavior() : undefined,
      v => [
        ...this.validateLinkedPageObjects(object, linkedList, v.targets, "単体警告の連動設定"),
      ],
    );
  }

  private validateGroupAlertAllow(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IObjectsControllable<ILinkedObjectGroupSpecifiable>>(
      instanceOfIGroupAlertAllowBehaviorPropertyEditable(object) ? object.groupAlertAllowBehavior() : undefined,
      v => [
        ...this.validateLinkedObjectGroups(object, linkedList, v.targets),
      ],
    );
  }

  private validateScriptExecuteTrigger(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IObjectsControllable<ILinkedPageObjectSpecifiable>>(
      instanceOfIScriptExecuteTriggerBehaviorPropertyEditable(object) ? object.scriptExecuteTriggerBehavior() : undefined,
      v => [
        ...this.validateLinkedPageObjects(object, linkedList, v.targets, "入力を反映する外部処理エリア"),
      ],
    );
  }

  private validateScriptExecute(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<ILinkedScriptExecuteBehavior>(
      instanceOfIScriptExecuteBehaviorPropertyEditable(object) ? object.scriptExecuteBehavior() : undefined,
      v => [
        ...this.validateLinkedPageObjects(object, linkedList, v.references, "連携項目"),
      ],
    );
  }

  private validateHospitalizedHisInformationItemSelect(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[]): IFormatAlert[] {
    return this.validateProperty<IHospitalizedHisInformationItemSelectBehavior>(
      instanceOfIHospitalizedHisInformationItemSelectBehaviorPropertyEditable(object) ? object.hospitalizedHisInformationItemSelectBehavior() : undefined,
      v => [
        v.baseDateReferBehavior.isEnable
          ? this.validateLinkedPageObject(object, linkedList, v.baseDateReferBehavior.reference, "入院期間の絞込みの基準日")
          : undefined,
      ],
    );
  }

  private validateProperty<T>(property: IPropertyEditable<T> | undefined, validate: (v: T) => IFormatAlert[]): IFormatAlert[] {
    return property && property.isEditable()
      ? validate(property.getValue()).filter(a => !!a)
      : [];
  }

  private validateLinkedObjects(object: IFormatObjectEditable, linkedList: ILinkedObjectSpecifiable[], targets: ILinkedObjectSpecifiable[], description: string, subDescription: string = "", shouldCompareId: boolean = true): IFormatAlert[] {
    return Enumerable.from(targets)
      .select((o, i) => !o || Enumerable.from(linkedList).any(l => this.compareLinkedObject(o, l, shouldCompareId))
        ? null
        : new ObjectAlert(object, "「" + description + "」で選択した" + (i + 1) + "行目のオブジェクト" +  subDescription + "は存在していません。"))
      .where(o => !!o)
      .toArray();
  }

  private validateLinkedObject(object: IFormatObjectEditable, linkedList: ILinkedObjectSpecifiable[], target: ILinkedObjectSpecifiable, description: string, subDescription: string = ""): IFormatAlert {
    if (!target) {
      // targetがnullかどうかは別のvalidatorでチェックしているのでここはスキップ
      return;
    }
    return Enumerable.from(linkedList).any(l => this.compareLinkedObject(target, l))
      ? null
      : new ObjectAlert(object, "「" + description + "」で選択したオブジェクト" +  subDescription + "は存在していません。");
  }

  private compareLinkedObject(a: ILinkedObjectSpecifiable, b: ILinkedObjectSpecifiable, compareId: boolean = true): boolean {
    return a.type === b.type &&
      (!compareId || a.id === b.id) &&
      a.key === b.key &&
      (typeof a.subKey !== "string" || !(a.type === "check" || a.type === "circleselect") || a.subKey === b.subKey) &&
      (typeof a.tableIndex !== "object" || typeof b.tableIndex !== "object" || (a.tableIndex.column === b.tableIndex.column && a.tableIndex.row === b.tableIndex.row)) &&
      (typeof a.isDate !== "boolean" || a.isDate === b.isDate);
  }

  private validateLinkedPageObjects(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[], targets: ILinkedPageObjectSpecifiable[], description: string, subDescription: string = "", compareId: boolean = true): IFormatAlert[] {
    return Enumerable.from(targets)
      .select((o, i) => !o || Enumerable.from(linkedList).any(l => this.compareLinkedPageObject(o, l, compareId))
        ? null
        : new ObjectAlert(object, "「" + description + "」で選択した" + (i + 1) + "行目のオブジェクト" +  subDescription + "は存在していません。"))
      .where(o => !!o)
      .toArray();
  }

  private validateLinkedPageObject(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[], target: ILinkedPageObjectSpecifiable, description: string, subDescription: string = ""): IFormatAlert {
    return Enumerable.from(linkedList).any(l => this.compareLinkedPageObject(target, l))
      ? null
      : new ObjectAlert(object, "「" + description + "」で選択したオブジェクト" +  subDescription + "は存在していません。");
  }

  private compareLinkedPageObject(a: ILinkedPageObjectSpecifiable, b: ILinkedPageObjectSpecifiable, compareId: boolean = true): boolean {
    // フォーマット仕様的には正確なpageKeyが指定されるべきだが、一致判定するとFINDEX製臨調フォーマットが全て違反になる。
    // (外部処理の生成をツールで一括で処理したようで、そのツールがpageKeyを1で追加してしまった模様。)
    // 実際、文書編集時(v1/v2Web)には見てないようなので、一致判定をやめる。
    // return a.pageKey === b.pageKey && this.compareLinkedObject(a, b, compareId);
    return this.compareLinkedObject(a, b, compareId);
  }

  private validateLineGraphItems(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[], items: ILineGraphItem[]): IFormatAlert[] {
    return Enumerable.from(items)
      .selectMany((o, i)  => this.validateLineGraphItem(object, linkedList, o, i + 1))
      .toArray();
  }

  private validateLineGraphItem(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[], item: ILineGraphItem, index: number): IFormatAlert[] {
    const alerts: IFormatAlert[] = [];
    Enumerable.from(item.pairs).forEach((o, i) => {
      alerts.push(this.validateLineGraphLinkedObjects(object, linkedList, index, i + 1, o.timeAxis, item.isTimeAxisDate ? "(時間)" : "(日付)"));
      alerts.push(this.validateLineGraphLinkedObjects(object, linkedList, index, i + 1, o.value, "(値)"));
    });
    return Enumerable.from(alerts)
      .where(o => !!o)
      .toArray();
  }

  private validateLineGraphLinkedObjects(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[], itemIndex: number, targetIndex: number, target: ILineGraphLinkedObjectSpecifiable, description: string): IFormatAlert {
    return Enumerable.from(linkedList).any(l => this.compareLineGraphLinkedObject(target, l))
      ? null
      : new ObjectAlert(object, "「折れ線グラフ項目」の項目" + itemIndex + "行目で選択した" + targetIndex + "行目のオブジェクト" +  description + "は存在していません。");
  }

  private compareLineGraphLinkedObject(a: ILineGraphLinkedObjectSpecifiable, b: ILinkedPageObjectSpecifiable): boolean {
    // フォーマット仕様的には正確なpageKeyが指定されるべきだが、文書編集時(v1/v2Web)には見ておらず、
    // 一致判定を行って登録できないフォーマットがあるといけないので一致判定をやめる。
    // return a.page === b.pageKey && this.compareLinkedObject(a, b);
    return this.compareLinkedObject(a, b);
  }

  private validateDateCalculateItems(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[], items: IDateCalculateItem[]): IFormatAlert[] {
    return Enumerable.from(items)
      .select((o, i) => !instanceOfIDateCalculateObject(o.reference) || Enumerable.from(linkedList).any(l => instanceOfIDateCalculateObject(o.reference) && this.compareDateCalculateItem(o.reference, l))
        ? null
        : new ObjectAlert(object, "「日付計算」で選択した" + (i === 0 ? "基準日" : i + "行目") + "のオブジェクトは存在していません。"))
      .where(o => !!o)
      .toArray();
  }

  private compareDateCalculateItem(a: IDateCalculateObject, b: ILinkedPageObjectSpecifiable): boolean {
    return a.type === b.type &&
      a.key === b.key;
  }

  private validateLinkedObjectGroups(object: IFormatObjectEditable, linkedList: ILinkedPageObjectSpecifiable[], targets: ILinkedObjectGroupSpecifiable[]): IFormatAlert[] {
    return Enumerable.from(targets)
      .select((o, i) => Enumerable.from(linkedList).any(l => this.compareLinkedObjectGroup(o, l))
        ? null
        : new ObjectAlert(object, "「グループ警告の連動設定」で選択した" + (i + 1) + "行目のオブジェクトは存在していません。"))
      .where(o => !!o)
      .toArray();
  }

  private compareLinkedObjectGroup(a: ILinkedObjectGroupSpecifiable, b: ILinkedPageObjectSpecifiable): boolean {
    return a.key === b.key;
  }
}
